import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, Check, ShieldCheck, Monitor, LifeBuoy, Mail, Wifi, ShieldAlert, CreditCard, Tv, HardDrive, Home, PhoneCall, Network, Shield, Satellite, Server, Cloud, Building2 } from "lucide-react";
import SEO from "../components/SEO";
import { services, PHONE, PHONE_TEL } from "../data/content";
import { getServiceFaqs } from "../data/serviceFaqs";

const iconMap = { Monitor, LifeBuoy, Mail, Wifi, ShieldAlert, ShieldCheck, CreditCard, Tv, HardDrive, Home, PhoneCall, Network, Shield, Satellite, Server, Cloud };

export function Services() {
  const homeServices = services.filter((s) => s.audience === "home");
  return (
    <>
      <SEO
        title="Home IT Services Adelaide | Computer Repairs, Wi-Fi, Email — Tech Emergency"
        description="Home IT services across Adelaide: on-site computer & laptop repairs, Wi-Fi and internet troubleshooting, home UniFi networking, email and Microsoft 365 setup, data recovery, virus removal, anti-scam help and senior-friendly tech support."
        path="/services"
      />
      <section className="pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ Home IT Services</div>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter leading-none mb-6">
            IT help for<br />your home.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed mb-6">
            From a frozen laptop in Glenelg to Wi-Fi that won't reach the back room — pick the
            service that fits, or just call us and we'll triage it on the phone for free.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="inline-flex items-center gap-2.5 px-4 py-3 border border-white/15 text-sm sm:text-base font-semibold text-white"
              style={{ background: "rgba(255,59,48,0.08)" }}
              data-testid="services-eftpos-note"
            >
              <img src="/images/tap-and-go.png" alt="Tap and go contactless payment" className="h-7 w-auto" />
              Easy Payment Onsite — EFTPOS or cash payment upon job completion.
            </div>
            <Link to="/pricing" data-testid="services-pricing-link" className="te-btn te-btn-primary">
              View our pricing <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {homeServices.map((s, i) => {
            const Icon = iconMap[s.icon] || ShieldCheck;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-link-${s.slug}`}
                className="te-card p-8 group relative"
              >
                <div className="te-overline text-[10px] absolute top-6 right-6">0{i + 1}</div>
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center border border-white/10">
                    <Icon size={22} style={{ color: "var(--te-primary)" }} />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight">{s.title}</h2>
                </div>
                <p className="text-zinc-400 leading-relaxed mb-5">{s.long}</p>
                <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--te-primary)" }}>
                  Details <ArrowRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-card p-8" data-testid="home-other-tech-note">
            <div className="te-overline mb-2">/ Something else around the home?</div>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-3">
              Need help with other technology around your home?
            </h2>
            <p className="text-zinc-400 max-w-3xl mb-5">
              Give us a call first to discuss what you need and we&apos;ll let you know if
              it&apos;s something we can assist with.
            </p>
            <a href={PHONE_TEL} data-testid="home-other-tech-call-btn" className="te-btn te-btn-primary">
              <Phone size={16} /> {PHONE}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = iconMap[service.icon] || ShieldCheck;
  const others = services.filter((s) => s.slug !== slug && s.audience === service.audience).slice(0, 3);
  const faqs = getServiceFaqs(slug);

  const jsonLd = faqs.length
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: service.title,
            description: service.short,
            url: `https://techemergency.com.au/services/${service.slug}`,
            provider: {
              "@type": "LocalBusiness",
              name: "Tech Emergency",
              telephone: "+611800836390",
              areaServed: { "@type": "City", name: "Adelaide" },
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      }
    : undefined;

  return (
    <>
      <SEO
        title={service.seoTitle || `${service.title} Adelaide | Tech Emergency — On-Site IT`}
        description={service.seoDescription || service.short}
        path={`/services/${service.slug}`}
        image={service.cover}
        jsonLd={jsonLd}
      />
      <section className="pt-16 pb-16 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `linear-gradient(rgba(5,5,5,0.85), rgba(5,5,5,0.95)), url(${service.cover})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/services" className="te-overline mb-6 inline-block hover:text-white">/ ← All services</Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 flex items-center justify-center" style={{ background: "var(--te-primary)" }}>
              <Icon size={26} color="#fff" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none">
              {service.title}
            </h1>
          </div>
          <p className="text-lg text-zinc-300 max-w-2xl leading-relaxed">{service.long}</p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="te-overline mb-4">/ What's included</div>
            <ul className="space-y-3">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 p-4 te-card">
                  <Check size={18} className="mt-1 flex-shrink-0" style={{ color: "var(--te-primary)" }} />
                  <span className="text-zinc-300">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <aside className="lg:col-span-5">
            <div className="te-card p-8 sticky top-24">
              <div className="te-overline mb-3">/ Ready to go?</div>
              <h3 className="text-2xl font-bold tracking-tight mb-4">
                Talk to a real local technician.
              </h3>
              <p className="text-sm text-zinc-400 mb-2">
                Free over-the-phone triage. We'll quote you upfront before any visit.
              </p>
              <p className="flex items-center gap-2 text-sm text-zinc-400 mb-6" data-testid="service-payment-note">
                <img src="/images/tap-and-go.png" alt="Tap and go contactless payment" className="h-6 w-auto" />
                Easy Payment Onsite — EFTPOS or cash payment upon job completion.
              </p>
              <a href={PHONE_TEL} data-testid="service-call-btn" className="te-btn te-btn-primary w-full mb-3">
                <Phone size={16} /> {PHONE}
              </a>
              <Link to="/book" data-testid="service-book-btn" className="te-btn te-btn-ghost w-full">
                Send an enquiry
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="te-overline mb-6">/ Frequently asked — {service.title}</div>
            <div className="space-y-3" data-testid="service-faqs">
              {faqs.map((f, i) => (
                <details key={i} className="te-card group" data-testid={`service-faq-${i}`}>
                  <summary className="cursor-pointer p-5 flex items-start justify-between gap-4 list-none">
                    <h3 className="text-base sm:text-lg font-semibold tracking-tight text-white pr-4">
                      {f.q}
                    </h3>
                    <span
                      className="flex-shrink-0 w-7 h-7 flex items-center justify-center border border-white/15 text-lg leading-none transition-transform group-open:rotate-45"
                      style={{ color: "var(--te-primary)" }}
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </summary>
                  <div className="px-5 pb-5 pt-0 text-zinc-300 leading-relaxed text-sm sm:text-base border-t border-white/5 mt-0">
                    <p className="pt-4">{f.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-6">/ Related services</div>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((o) => {
              const OIcon = iconMap[o.icon] || ShieldCheck;
              return (
                <Link key={o.slug} to={`/services/${o.slug}`} className="te-card p-6">
                  <OIcon size={20} style={{ color: "var(--te-primary)" }} className="mb-3" />
                  <h4 className="font-semibold mb-1">{o.title}</h4>
                  <p className="text-sm text-zinc-400">{o.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
